import { useRef, useState, useEffect } from 'react'

export function LastPageReveal({ isActive, text }) {
  const [revealProgress, setRevealProgress] = useState(0)
  const [swapProgress, setSwapProgress] = useState(0)
  const [phase, setPhase] = useState('idle') // idle | revealing | holding | swapping | done
  const rafRef = useRef(0)
  const swapRafRef = useRef(0)
  const holdTimeoutRef = useRef(0)

  const REVEAL_DURATION = 2500
  const HOLD_DURATION = 3000
  const SWAP_DURATION = 2000

  // Easing (smooth end so it doesn't feel cut off)
  const easeInOut = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2)

  useEffect(() => {
    // Cleanup helper
    const cancelAll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      if (swapRafRef.current) cancelAnimationFrame(swapRafRef.current)
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current)
    }

    if (!isActive) {
      cancelAll()
      setRevealProgress(0)
      setSwapProgress(0)
      setPhase('idle')
      return
    }

    // Start reveal animation
    setPhase('revealing')
    const start = performance.now()
    const tick = (t) => {
      const linear = Math.min((t - start) / REVEAL_DURATION, 1)
      const eased = easeInOut(linear)
      setRevealProgress(eased)
      if (linear < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        // Hold phase
        setPhase('holding')
        holdTimeoutRef.current = setTimeout(() => {
          // Begin swap (unreveal + heart reveal)
          setPhase('swapping')
          const swapStart = performance.now()
          const swapTick = (ts) => {
            const raw = Math.min((ts - swapStart) / SWAP_DURATION, 1)
            const easedSwap = easeInOut(raw)
            setSwapProgress(easedSwap)
            if (raw < 1) {
              swapRafRef.current = requestAnimationFrame(swapTick)
            } else {
              setPhase('done')
            }
          }
          swapRafRef.current = requestAnimationFrame(swapTick)
        }, HOLD_DURATION)
      }
    }
    rafRef.current = requestAnimationFrame(tick)

    return () => {
      cancelAll()
    }
  }, [isActive])

  if (!isActive && phase === 'idle') return null

  const getMask = (p) => {
    if (p <= 0) {
      // Fully hidden
      return 'linear-gradient(135deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 100%)'
    }
    const a = p * 70
    const b = a + 20
    return `linear-gradient(135deg, rgba(0,0,0,1) 0%, rgba(0,0,0,1) ${a}%, rgba(0,0,0,0) ${b}%, rgba(0,0,0,0) 100%)`
  }

  // Determine effective progress for text and heart
  let textProgress = 0
  if (phase === 'revealing' || phase === 'holding') textProgress = revealProgress
  else if (phase === 'swapping') textProgress = 1 - swapProgress // unreveal
  else if (phase === 'done') textProgress = 0

  let heartProgress = 0
  if (phase === 'swapping') heartProgress = swapProgress
  else if (phase === 'done') heartProgress = 1

  const baseMaskStyle = (p) => ({
    WebkitMaskImage: getMask(p),
    maskImage: getMask(p),
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskSize: '150% 150%',
    maskSize: '150% 150%',
    WebkitMaskPosition: `${-50 + p * 100}% ${-50 + p * 100}%`,
    maskPosition: `${-50 + p * 100}% ${-50 + p * 100}%`
  })

  // Smooth fade-out for last tiny remainder instead of instant cut
  const textOpacity =
    phase === 'swapping'
      ? Math.max(0, 1 - swapProgress * 1.05) // slightly faster fade near end
      : phase === 'done'
      ? 0
      : 1

  return (
    <div
      className="fade-in"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 5
      }}>
      <div
        style={{
          position: 'relative',
          width: '80%',
          fontFamily: "'Poppins', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        {/* Text layer */}
        <div
          style={{
            fontSize: '48px',
            fontWeight: 600,
            color: 'white',
            whiteSpace: 'pre-line',
            opacity: textOpacity,
            transition: phase === 'swapping' ? 'opacity 120ms linear' : 'none',
            ...baseMaskStyle(textProgress)
          }}>
          {text}
        </div>
        {/* Heart layer */}
        <div
          style={{
            position: 'absolute',
            fontSize: '72px',
            filter: 'drop-shadow(0 0 12px rgba(255,0,0,0.4))',
            opacity: heartProgress === 0 ? 0 : 1,
            transform: `scale(${0.8 + heartProgress * 0.2})`,
            transition: 'transform 0.3s ease-out, opacity 0.4s ease-out',
            ...baseMaskStyle(heartProgress)
          }}>
          ❤️
        </div>
      </div>
    </div>
  )
}
